import React from 'react';
import { CheckCircle2, Package, Pencil, Plus, Trash2 } from 'lucide-react';

import { formatIST, itemsLabel } from '@/lib/utils';
import { getProductTypeName } from '@/lib/constants';
import { STATUS_MAP, PRIORITY_MAP } from '@/lib/status-colors';
import {
  CardAction,
  ChipRow,
  EmptyState,
  PageHeader,
  Pager,
  PhonePage,
  PillButton,
  ScreenLoader,
  SearchBar,
  StatusPill,
} from '@/components/phone/primitives';
import { Colors, priorityTone, statusTone } from '@/components/phone/theme';

/**
 * Every order, on the admin's phone.
 *
 * The desktop list is ten columns at 30px a row, which is the right shape for
 * forty orders on a laptop and a sideways scroll on a handset. Here each order
 * is a card that says what it is, who it is for, where it stands and when it is
 * due, in that order, and the phase counts are chips that filter the list.
 *
 * Selecting several orders at once and printing the payment sheet are desk
 * errands and stay on the desktop; a row here offers edit, mark delivered and
 * delete one at a time.
 */

const FINISHED = ['delivered', 'stocked'];

function OrderCard({ order, showFinancials, showEdit, showComplete, isAdmin, onOpen, onEdit, onComplete, onDelete }) {
  const finished = FINISHED.includes(order.status);
  const canComplete = showComplete && !finished;
  const who = [order.firm_name || order.customer_name, order.firm_name && order.customer_name]
    .filter(Boolean).join(' — ');

  return (
    <li
      data-testid={`order-card-${order.order_id}`}
      className="mt-3 rounded-[10px] border"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <button
        type="button"
        onClick={onOpen}
        data-testid={`view-order-${order.order_id}`}
        className="block w-full px-4 pt-3 text-left"
      >
        <span className="line-clamp-2 block text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
          {itemsLabel(order.line_items, order.customer_name)}
        </span>
        <span className="mt-0.5 block truncate text-[13px] font-medium" style={{ color: Colors.text.secondary }}>
          {who || '—'}
        </span>
        <span className="mt-0.5 block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
          {order.order_id}
          {order.dept_code ? ` · ${getProductTypeName(order.dept_code)}` : ''}
          {order.created_at ? ` · ${formatIST(order.created_at, { dateOnly: true })}` : ''}
        </span>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <StatusPill
            testID={`order-status-${order.order_id}`}
            label={order.location?.label || STATUS_MAP[order.status]?.label || order.status}
            tone={statusTone(order.status)}
            dot
          />
          {order.location?.others > 0 && (
            <span className="text-[11px]" style={{ color: Colors.text.tertiary }}>+{order.location.others} more</span>
          )}
          {!!order.priority && (
            <StatusPill label={PRIORITY_MAP[order.priority]?.label || order.priority} tone={priorityTone(order.priority)} />
          )}
        </div>

        <div className="flex items-center justify-between gap-2 py-3 text-[12px]">
          <span style={{ color: Colors.text.secondary }}>
            {order.expected_delivery_date
              ? `Due ${formatIST(order.expected_delivery_date, { dateOnly: true })}`
              : 'No delivery date'}
          </span>
          {showFinancials && order.order_value != null && (
            <span className="font-semibold tabular-nums" style={{ color: Colors.text.primary }}>
              ₹{order.order_value.toLocaleString()}
            </span>
          )}
        </div>
      </button>

      {(showEdit || canComplete || isAdmin) && (
        <div className="flex items-center gap-2 border-t px-3 py-2" style={{ borderColor: Colors.border.subtle }}>
          {showEdit && <CardAction icon={Pencil} label="Edit" onClick={onEdit} testID={`edit-order-${order.order_id}`} />}
          {canComplete && (
            <CardAction icon={CheckCircle2} label="Delivered" tone="positive" onClick={onComplete}
              testID={`complete-order-${order.order_id}`} />
          )}
          {isAdmin && (
            <CardAction icon={Trash2} label="Delete" tone="critical" onClick={onDelete}
              testID={`delete-order-${order.order_id}`} className="ml-auto" />
          )}
        </div>
      )}
    </li>
  );
}

export default function PhoneOrdersList({
  orders, total, loading, error, page, totalPages, onPage, onSearch,
  phases, scopes, scope, statusFilter, statusCounts, onScope, onStatus,
  priorityFilter, onPriority,
  showFinancials, showEdit, showComplete, isAdmin,
  onRefresh, onNew, onOpen, onEdit, onComplete, onDelete,
}) {
  // a scope and a phase can share a name — Delivered is both — so the scope
  // chips carry a prefix and the two never collide in one row
  const chips = [
    ...scopes.map(([key, label]) => ({ key: `scope:${key}`, label })),
    ...phases.map(([key, label]) => ({ key, label: `${label} ${statusCounts[key] || 0}` })),
  ];
  const priorities = [
    { key: null, label: 'Any priority' },
    ...['high', 'medium', 'low'].map((level) => ({ key: level, label: PRIORITY_MAP[level].label })),
  ];

  return (
    <PhonePage testID="orders-page">
      <PageHeader
        title="Orders"
        subtitle={total ? `${total.toLocaleString()} total` : undefined}
        action={(
          <PillButton onClick={onNew} testID="create-order-btn">
            <Plus className="h-4 w-4" /> New
          </PillButton>
        )}
      />

      <div className="px-5">
        <SearchBar placeholder="Order ID, voice number, customer…" onChange={onSearch} testID="orders-search-input" />
        <div className="mt-3">
          <ChipRow
            chips={chips}
            active={statusFilter || `scope:${scope}`}
            onSelect={(key) => (key.startsWith('scope:') ? onScope(key.slice(6)) : onStatus(key))}
            testIDPrefix="chip"
          />
        </div>
        <div className="mt-2">
          <ChipRow chips={priorities} active={priorityFilter} onSelect={onPriority} testIDPrefix="priority" />
        </div>
      </div>

      <div className="flex-1 px-5 pb-8">
        {loading ? (
          <ScreenLoader />
        ) : error ? (
          <div className="py-16 text-center">
            <p className="text-[15px] font-medium" style={{ color: Colors.status.error }}>Failed to load orders</p>
            <p className="mt-1 text-[13px]" style={{ color: Colors.text.tertiary }}>{error}</p>
            <button
              type="button"
              onClick={onRefresh}
              className="mt-4 rounded-[10px] border px-5 py-2 text-[13px] font-bold"
              style={{ borderColor: Colors.border.default, color: Colors.accent.DEFAULT }}
            >
              Try again
            </button>
          </div>
        ) : orders.length === 0 ? (
          <EmptyState icon={Package} message="No orders found" hint="Try another filter, or book the first one." />
        ) : (
          <ul data-testid="orders-list">
            {orders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                showFinancials={showFinancials}
                showEdit={showEdit}
                showComplete={showComplete}
                isAdmin={isAdmin}
                onOpen={() => onOpen(order)}
                onEdit={() => onEdit(order)}
                onComplete={() => onComplete(order)}
                onDelete={() => onDelete(order)}
              />
            ))}
          </ul>
        )}
        <Pager page={page} totalPages={totalPages} onPage={onPage} testID="orders-pagination" />
      </div>
    </PhonePage>
  );
}
